import type { ApolloClient } from '@apollo/client';

import { JOB } from './api';
import type { Extraction } from './extraction';
import type { Transcript } from './transcript';

/** A job's JSON artifacts, by job and kind, fetched at most once per session. */
const cache = new Map<string, Promise<unknown>>();

function load<T>(key: string, url: string): Promise<T> {
  let pending = cache.get(key) as Promise<T> | undefined;
  if (!pending) {
    pending = fetch(url).then((response) => {
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      return response.json() as Promise<T>;
    });
    pending.catch(() => cache.delete(key));
    cache.set(key, pending);
  }
  return pending;
}

export function loadExtraction(jobId: string, url: string): Promise<Extraction> {
  return load(jobId, url);
}

export function loadTranscript(jobId: string, url: string): Promise<Transcript> {
  return load(`${jobId}:transcript`, url);
}

/** Warm a finished job before it is opened: its record, signed links and extraction. */
export async function prefetchJob(client: ApolloClient, jobId: string): Promise<void> {
  if (cache.has(jobId)) {
    return;
  }
  try {
    const { data } = await client.query({ query: JOB, variables: { id: jobId } });
    const downloads = data?.job.result?.downloads;
    await Promise.all([
      downloads?.extraction ? loadExtraction(jobId, downloads.extraction) : null,
      downloads?.transcript ? loadTranscript(jobId, downloads.transcript) : null,
    ]);
  } catch {
    // A prefetch is a guess; opening the job fetches for real.
  }
}
